import type { OnChange } from '@monaco-editor/react'
import { Editor } from '@monaco-editor/react'
import EditIcon from '@mui/icons-material/Edit'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { JsonEditor } from '@/features/controller/configuration/components/textEditor/JsonEditor'
import type { ControllerConfigTypeId, TextEditorLanguage } from '@/features/controller/configuration/types'
import { useMonacoThemes } from '@/features/controller/configuration/utils/editor/hooks/useMonacoThemes'
import { EDITOR_OPTIONS, MONACO_THEMES } from '@/features/controller/configuration/utils/editor/monacoThemes'
import { JSON_SCHEMA_MAP } from '@/features/controller/configuration/utils/validateConfigText'

interface TextEditorProps {
  language: TextEditorLanguage
  onChange?: OnChange
  refreshTrigger?: number
  value: string
  configTypeId?: ControllerConfigTypeId | null
  onValidationChange?: (isValid: boolean) => void
  schema?: object | null
}

const JSON_HELP_CONTENT = `JSON Configuration Help

SYNTAX RULES:
• Use double quotes for all strings: "model": "value"
• No trailing commas: {"a": 1, "b": 2} ✓ not {"a": 1, "b": 2,}
• Objects use {}, arrays use []
• Nested structures must be properly closed

KEYBOARD SHORTCUTS:
• Ctrl/Cmd+Space: trigger autocomplete/suggestions (For some Mac systems try Cmd+I)
• Tab: accept selected suggestion
• Esc: close suggestion popup
• ↑↓: navigate suggestion list
• Shift+Alt+F (Shift+Option+F on Mac): format document
• Ctrl/Cmd+F: find and replace

SCHEMA FEATURES:
• Type opening quote (") to see valid property names
• Autocomplete shows available enum values
• Red underlines indicate validation errors
• Hover over properties to see descriptions

TIPS:
• Start typing property names for intelligent suggestions
• Use Ctrl/Cmd+Space if suggestions don't appear automatically
• Validation errors appear in real-time as you type`

// Mode Indicator Configuration
const MODE_INDICATORS = {
  editMode: {
    icon: EditIcon,
    label: 'Edit Mode',
    color: 'primary' as const,
    variant: 'filled' as const,
  },
  readonlyMode: {
    icon: VisibilityIcon,
    label: 'Read Only',
    color: 'default' as const,
    variant: 'outlined' as const,
  },
} as const

// Container Styles Configuration
const CONTAINER_STYLES = {
  editMode: {
    border: '2px solid',
    borderColor: 'primary.main',
    backgroundColor: 'background.paper',
    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.2)',
    '&:hover': {
      borderColor: 'primary.dark',
      boxShadow: 1,
    },
  },
  readonlyMode: {
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'grey.50',
  },
  common: {
    borderRadius: 1,
    transition: 'all 0.3s ease-in-out',
  },
} as const

/**
 * Text editor/viewer with two modes: edit and read-only.
 */
export function TextEditor({
  language,
  value,
  onChange,
  refreshTrigger,
  configTypeId,
  onValidationChange,
  schema,
}: Readonly<TextEditorProps>) {
  const { t } = useTranslation()
  const [editorKey, setEditorKey] = useState(0)
  const { themesReady, monaco } = useMonacoThemes()

  const isReadOnly = !onChange
  const isEditMode = !isReadOnly
  const isJson = language === 'json'
  const staticSchema = configTypeId != null ? JSON_SCHEMA_MAP[configTypeId] : null
  const effectiveSchema = schema ?? staticSchema
  const hasJsonSchema = isJson && effectiveSchema != null
  const showJsonHelp = isJson && isEditMode

  // Get current mode configuration
  const currentMode = isEditMode ? 'editMode' : 'readonlyMode'

  useEffect(() => {
    if (refreshTrigger && themesReady && monaco) {
      setEditorKey((prev) => prev + 1)
    }
  }, [refreshTrigger, themesReady, monaco, currentMode])

  const modeIndicator = MODE_INDICATORS[currentMode]
  const containerStyles = {
    ...CONTAINER_STYLES.common,
    ...CONTAINER_STYLES[currentMode],
  }

  const baseEditorOptions = {
    ...EDITOR_OPTIONS.base,
    ...EDITOR_OPTIONS[currentMode],
    readOnly: isReadOnly,
  }

  const IconComponent = modeIndicator.icon

  const renderEditor = () => {
    if (hasJsonSchema) {
      return (
        <JsonEditor
          baseOptions={baseEditorOptions}
          height={500}
          schema={effectiveSchema}
          theme={MONACO_THEMES[currentMode].name}
          value={value}
          onChange={onChange}
          onValidationChange={onValidationChange}
        />
      )
    }

    return (
      <Editor
        key={`editor-${currentMode}-${editorKey}`}
        height={500}
        language={language}
        options={baseEditorOptions}
        theme={MONACO_THEMES[currentMode].name}
        value={value}
        onChange={onChange}
      />
    )
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Mode Indicator and JSON Help */}
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          left: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Chip
          color={modeIndicator.color}
          icon={<IconComponent />}
          label={modeIndicator.label}
          size="small"
          variant={modeIndicator.variant}
        />

        {showJsonHelp && (
          <Tooltip
            arrow
            placement="bottom-start"
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 550,
                  backgroundColor: 'rgba(0, 0, 0, 0.92)',
                  '& .MuiTooltip-arrow': {
                    color: 'rgba(0, 0, 0, 0.92)',
                  },
                },
              },
            }}
            title={
              <Box
                sx={{
                  whiteSpace: 'pre-line',
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                  fontFamily: 'monospace',
                }}
              >
                {JSON_HELP_CONTENT}
              </Box>
            }
          >
            <IconButton
              size="small"
              sx={{
                padding: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <HelpOutlineIcon color="action" fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Editor Container with Visual Feedback */}
      <Box sx={containerStyles}>
        {themesReady ? (
          renderEditor()
        ) : (
          <Box sx={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress title={t('controllers.configuration.loading_editor')} />
          </Box>
        )}
      </Box>
    </Box>
  )
}
